/**
 * Add/Edit dialog for a product Default Target.
 *
 * Ported from the legacy DefaultTargetAdd/DefaultTargetEdit pages. Field order
 * and calculation mirror the old system:
 *   Calibration (read-only)            - product's active calibration
 *   Strength (unit)                    - base strength, user-entered
 *   Safety Margin (%)                  - user-entered
 *   Target Description                 - required (stored as Purpose)
 *   Strength + Safety Margin (unit)    - computed: base * (1 + margin/100)
 *   Maturity Target (°C·hrs or hrs)    - computed from the calibration regression
 *
 * The applied strength and EA target are derived with the shared
 * regressionConversion helpers (EA = exp((Strength - A) / B)). When the active
 * calibration has no usable regression, EA becomes a manual field.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  InputAdornment,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import productsService, {
  type DefaultTarget,
  type TargetCreate,
} from '../../services/products';
import { eaFromStrength, applySafetyMargin, type CurveType } from '../../utils/regressionConversion';
import { MATURITY_UNIT_UNKNOWN, knownMaturityMeta } from '../../utils/maturityDisplay';
import { apiErrorText } from '../../utils/apiError';
import { targetRangeFlag } from '../../utils/maturityDisplay';
import type { CalibratedRange } from '../../services/pours';

export interface ActiveCalibration {
  id: string;
  name: string;
  /** Chosen trend curve; regressionA/B carry that curve's coefficients. */
  curveType: CurveType;
  regressionA: number | null;
  regressionB: number | null;
}

interface Props {
  open: boolean;
  productId: string;
  groupId: string;
  editing: DefaultTarget | null;
  /** The product's active calibration, or null when none is linked/active. */
  calibration: ActiveCalibration | null;
  /** Strength unit resolved from the mix design company's setting (MPa /
   *  Psi / N/mm²); null while unknown, when no unit is shown. */
  strengthUnit?: string | null;
  /** The mix design's maturity method; names the target's maturity unit
   *  (TTF in °C·hrs or EA in hrs). Null or absent when not known. */
  maturityMethod?: string | null;
  /** Arrhenius specified temperature, so the label reads "Equivalent Age at Ts °C". */
  specifiedTemp?: number | null;
  /** The active calibration's tested range: a strength outside it is warned (allowed). */
  testedRange?: CalibratedRange | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onSaved: () => void;
}

/** Applied strength as a display string ('' when the base is blank/invalid). */
const computeApplied = (baseStrength: string, safetyMargin: string): string => {
  const base = parseFloat(baseStrength);
  if (baseStrength.trim() === '' || Number.isNaN(base)) return '';
  const margin = safetyMargin.trim() === '' ? 0 : parseFloat(safetyMargin);
  if (Number.isNaN(margin)) return '';
  return String(applySafetyMargin(base, margin));
};

const TargetDialog: React.FC<Props> = ({
  open,
  productId,
  groupId,
  editing,
  calibration,
  strengthUnit,
  maturityMethod,
  specifiedTemp,
  testedRange,
  onClose,
  onSuccess,
  onError,
  onSaved,
}) => {
  // "TTF Target" in °C·hrs or "EA Target" in hrs when the mix design's
  // method is known; otherwise the label names both.
  const maturity = knownMaturityMeta(maturityMethod, specifiedTemp);
  const maturityLabel = maturity
    ? `${maturity.index_label} target`
    : `Maturity Target, ${MATURITY_UNIT_UNKNOWN}`;
  const strengthAdornment = strengthUnit ? (
    <InputAdornment position="end">{strengthUnit}</InputAdornment>
  ) : undefined;
  const [purpose, setPurpose] = useState('');
  const [baseStrength, setBaseStrength] = useState('');
  const [safetyMargin, setSafetyMargin] = useState('');
  const [appliedStrength, setAppliedStrength] = useState('');
  const [eaTarget, setEaTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ purpose?: boolean; baseStrength?: boolean }>({});

  const isEdit = editing !== null;
  const hasCalibration = calibration !== null;
  const hasRegression =
    calibration?.regressionA != null &&
    calibration?.regressionB != null &&
    calibration.regressionB !== 0;
  // EA auto-calculates only from an active calibration that has a usable regression.
  const autoCalc = hasCalibration && hasRegression;

  // ---- Prefill on open ----
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const base = editing.base_strength?.toString() ?? '';
      const margin = editing.safety_margin?.toString() ?? '';
      setPurpose(editing.purpose ?? '');
      setBaseStrength(base);
      setSafetyMargin(margin);
      setAppliedStrength(editing.strength?.toString() ?? computeApplied(base, margin));
      setEaTarget(editing.target?.toString() ?? '');
    } else {
      setPurpose('');
      setBaseStrength('');
      setSafetyMargin('');
      setAppliedStrength('');
      setEaTarget('');
    }
    setErrors({});
  }, [open, editing]);

  // Applied strength (and, when auto-calculating, the EA target) recompute live.
  useEffect(() => {
    const applied = computeApplied(baseStrength, safetyMargin);
    setAppliedStrength(applied);
    if (autoCalc && applied !== '' && Number(applied) > 0) {
      const ea = eaFromStrength(Number(applied), calibration!.regressionA, calibration!.regressionB, calibration!.curveType);
      setEaTarget(ea != null ? String(ea) : '');
    }
    // Manual-EA mode: leave whatever the user typed untouched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseStrength, safetyMargin, autoCalc]);

  const handleSave = async () => {
    const nextErrors = {
      purpose: !purpose.trim(),
      baseStrength: baseStrength.trim() === '' || Number.isNaN(Number(baseStrength)),
    };
    setErrors(nextErrors);
    if (nextErrors.purpose || nextErrors.baseStrength) return;

    const applied = computeApplied(baseStrength, safetyMargin);
    const payload: TargetCreate = {
      purpose: purpose.trim(),
      base_strength: Number(baseStrength),
      safety_margin: safetyMargin.trim() === '' ? 0 : Number(safetyMargin),
      strength: applied === '' ? undefined : Number(applied),
      target: eaTarget.trim() === '' ? undefined : Number(eaTarget),
      calibration_id: calibration?.id,
    };

    setSaving(true);
    try {
      if (editing) {
        await productsService.updateTarget(productId, groupId, editing.target_id, payload);
        onSuccess('Target updated');
      } else {
        await productsService.createTarget(productId, groupId, payload);
        onSuccess('Target created');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      onError(apiErrorText(err, 'Failed to save target'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Target' : 'Add Target'}</DialogTitle>
      <DialogContent sx={{ '&.MuiDialogContent-root': { pt: 2 } }}>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* Calibration (read-only) */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="text.secondary">
              Calibration
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {calibration ? calibration.name : 'No active calibration'}
            </Typography>
          </Grid>

          {/* Warning when EA cannot be auto-calculated */}
          {!autoCalc && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning">
                {hasCalibration
                  ? 'The active calibration has no regression yet. Enter the maturity target manually.'
                  : 'No active calibration for this product, so the maturity target is not auto-calculated. Enter it manually.'}
              </Alert>
            </Grid>
          )}

          {/* Purpose (first row, matches the list column header) */}
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              required
              label="Purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              error={errors.purpose}
              helperText={errors.purpose ? 'Purpose is required' : undefined}
              inputProps={{ maxLength: 50 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {/* Strength (base) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Strength"
              value={baseStrength}
              onChange={(e) => setBaseStrength(e.target.value)}
              error={errors.baseStrength}
              helperText={errors.baseStrength ? 'A valid strength is required' : undefined}
              InputProps={{ endAdornment: strengthAdornment }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {/* Safety margin */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Safety Margin"
              value={safetyMargin}
              onChange={(e) => setSafetyMargin(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {/* Applied strength (computed, read-only) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Strength + Safety Margin"
              value={appliedStrength}
              InputProps={{
                readOnly: true,
                endAdornment: strengthAdornment,
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {(() => {
            const flag = targetRangeFlag(appliedStrength === '' ? null : Number(appliedStrength), testedRange);
            if (!flag || !testedRange) return null;
            const unit = strengthUnit ? ` ${strengthUnit}` : '';
            return (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  {appliedStrength}{unit} is {flag} the strength range the active calibration was tested over
                  ({testedRange.s_min}{unit} to {testedRange.s_max}{unit}). You can keep it, but its maturity target is
                  an extrapolation of the curve.
                </Alert>
              </Grid>
            );
          })()}

          {/* EA target (auto from calibration, or manual when none) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={maturityLabel}
              value={eaTarget}
              onChange={(e) => setEaTarget(e.target.value)}
              InputProps={{
                readOnly: autoCalc,
                endAdornment: maturity ? (
                  <InputAdornment position="end">{maturity.unit}</InputAdornment>
                ) : undefined,
              }}
              helperText={autoCalc ? 'Auto-calculated from the active calibration' : 'Enter the maturity target manually'}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TargetDialog;
